// frontend/src/services/saleService.ts
import api from './api';

export type SaleType = 'SALE' | 'RENT' | 'ABONO' | 'LIQUIDACION';
export type PaymentMethod = 'CASH' | 'CARD' | 'TRANSFER' | 'MERCADO_PAGO' | 'KUESKI' | 'Mixto';
export type SaleStatus = 'PENDING' | 'COMPLETED' | 'CANCELLED' | 'COMPLETADA' | 'PENDIENTE' | 'PRE-ORDER' | 'APARTADO' | 'CANCELADA';

export interface SaleItem {
  productId: number;
  productCode?: string;
  productName: string;
  quantity: number;
  unitPrice: number;
  total: number;
  product?: any;
  inventoryType?: string | null;
}

export interface SalePayment {
  id: number;
  saleId: number;
  method: string;
  amount: number;
  reference?: string;
  userId?: number;
  user?: { username?: string };
  notas?: string;
  status?: string;
  createdAt: string;
}

export interface Sale {
  id: number;
  folio?: string;
  customerName: string;
  customerEmail?: string;
  customerPhone?: string;
  customerId?: string;
  customerRecordId?: number;
  subtotal: number;
  tax: number;
  discount: number;
  discountPercentage?: number;
  discountReason?: string;
  amountPaid?: number;
  total: number;
  status: SaleStatus;
  paymentMethod: PaymentMethod;
  payments: SalePayment[];
  saleType: SaleType;
  needsAdjustments: boolean;
  adjustmentDetails?: string;
  inventoryDeducted: boolean;
  notes?: string;
  sellerId: number;
  ticketId?: number;
  createdAt: string;
  eventDate?: string;
  updatedAt: string;
  items: SaleItem[];
  seller?: {
    id_usuario: number;
    username?: string;
    email?: string;
  };
  ticket?: {
    id: number;
    title: string;
    status: string;
  };
  shippingCost?: number;
  alterationCost?: number;
  dias_transcurridos?: number;
  parentSaleId?: number | null;
  parentSale?: Sale | null;
  childSales?: Sale[];
  saleContext?: {
    rootSaleId: number;
    rootFolio: string;
    totalGlobal: number;
    totalPaidGlobal: number;
    balance: number;
    isNote: boolean;
    parentStatus: string;
  };
  paymentTimeline?: any[];
}

export interface CreateSaleRequest {
  customerName: string;
  customerEmail?: string;
  customerPhone?: string;
  customerId?: string;
  items: SaleItem[];
  paymentMethod: PaymentMethod;
  payments?: { method: PaymentMethod; amount: number }[];
  saleType: SaleType;
  needsAdjustments?: boolean;
  adjustmentDetails?: string;
  deductInventory?: boolean;
  notes?: string;
  sellerCode?: string;
  sellerPassword?: string;
  discountPercentage?: number;
  discountReason?: string;
  amountPaid?: number;

  isLayaway?: boolean;
  eventDate?: string;
  shippingCost?: number;
  alterationCost?: number;
}

export interface SalesResponse {
  data: Sale[];
  total: number;
  totalGeneral?: number;
  totalMonth?: number;
  limit: number;
  offset: number;
  message?: string; // Sometimes response wraps data in data property but sometimes direct? Check usage. Assumed consistent.
}

export interface SaleResponse {
  sale: Sale;
  message?: string;
}

class SaleService {
  // Crear venta
  async createSale(data: CreateSaleRequest): Promise<SaleResponse> {
    const response = await api.post('/sales', data);
    return response.data;
  }

  // Crear venta con productos nuevos (temporales) - Creación atómica
  async createPreOrderSale(data: any): Promise<SaleResponse> {
    const response = await api.post('/sales/pre-order', data);
    return response.data;
  }

  // Obtener todas las ventas
  async getSales(params?: {
    status?: SaleStatus;
    saleType?: SaleType;
    paymentMethod?: PaymentMethod;
    startDate?: string;
    endDate?: string;
    limit?: number;
    offset?: number;
    comisiones?: any[];
    parentSaleId?: number | null;
    parentSale?: Sale | null;
    childSales?: Sale[];
    customerName?: string;
    q?: string;
  }): Promise<SalesResponse> {
    const response = await api.get('/sales', { params });
    return response.data;
  }

  // Obtener una venta por ID
  async getSaleById(id: number): Promise<SaleResponse> {
    const response = await api.get(`/sales/${id}`);
    return response.data;
  }

  // Vincular venta con ticket
  async linkSaleToTicket(saleId: number, ticketId: number): Promise<SaleResponse> {
    const response = await api.patch(`/sales/${saleId}/link-ticket`, { ticketId });
    return response.data;
  }

  // Cancelar venta
  async cancelSale(id: number, reason: string): Promise<SaleResponse> {
    const response = await api.patch(`/sales/${id}/cancel`, { reason });
    return response.data;
  }

  // Actualizar Vendedora (Admin)
  async updateSaleSeller(saleId: number, newSellerId: string | number): Promise<any> {
    const response = await api.patch(`/sales/${saleId}/seller`, { newSellerId });
    return response.data;
  }

  // Actualizar estado (ej. completar apartado)
  async updateSaleStatus(id: number, status: string, paymentMethod?: PaymentMethod, payments?: any[]): Promise<SaleResponse> {
    const response = await api.patch(`/sales/${id}/status`, { status, paymentMethod, payments });
    return response.data;
  }

  // Modificación de abonos / pagos progresivos
  async addPayment(saleId: number, data: { amount: number; method: PaymentMethod; reference?: string; notas?: string }): Promise<any> {
    const response = await api.post(`/sales/${saleId}/payments`, data);
    return response.data;
  }

  async editPayment(saleId: number, paymentId: number, data: { amount: number; method: PaymentMethod; reference?: string; notas?: string }): Promise<any> {
    const response = await api.put(`/sales/${saleId}/payments/${paymentId}`, data);
    return response.data;
  }

  async deletePayment(saleId: number, paymentId: number): Promise<any> {
    const response = await api.delete(`/sales/${saleId}/payments/${paymentId}`);
    return response.data;
  }

  // Intercambiar artículo (Admin)
  async swapSaleItem(saleId: number, itemId: number, newProductId: number, newInventoryType: string = 'maestro'): Promise<any> {
    const response = await api.put(`/sales/${saleId}/items/${itemId}/swap`, { newProductId, newInventoryType });
    return response.data;
  }
  // Actualizar tipo de inventario (Admin)
  async updateSaleItemInventoryType(saleId: number, itemId: number, newInventoryType: string): Promise<any> {
    const response = await api.patch(`/sales/${saleId}/items/${itemId}/inventory-type`, { newInventoryType });
    return response.data;
  }

  // Actualizar precio de artículo (Admin)
  async updateSaleItemPrice(saleId: number, itemId: number, newPrice: number, reason: string): Promise<any> {
    const response = await api.patch(`/sales/${saleId}/items/${itemId}/price`, { newPrice, reason });
    return response.data;
  }

  // Cancelar un artículo individual de la venta (Admin)
  async cancelSaleItem(saleId: number, itemId: number, reason: string): Promise<any> {
    const response = await api.patch(`/sales/${saleId}/items/${itemId}/cancel`, { reason });
    return response.data;
  }

  // Crear nota de pago (Abono/Liquidación)
  async createPaymentNote(saleId: number, data: { amount?: number; method?: PaymentMethod; payments?: { amount: number; method: PaymentMethod }[]; notes?: string }): Promise<any> {
    const response = await api.post(`/sales/${saleId}/payment-note`, data);
    return response.data;
  }
}

export const saleService = new SaleService();
export default saleService;
